import React, { useState, useRef, useEffect } from "react";
import {
  Draggable,
  DraggableProvided,
  DraggableStateSnapshot
} from "@hello-pangea/dnd";
import { createPortal } from "react-dom";
import { cn } from "@/utils/utils";
import OptionsDropdownMenu, {
  DetailedViewOptions
} from "@/components/controls/OptionsDropdownMenu/OptionsDropdownMenu";
import Text from "@/components/ui/text";
import { useTranslation } from "react-i18next";
import { GripVertical } from "lucide-react";
import { Button } from "@/components/ui/button";
import Input from "@/components/ui/input";

export interface ReasonRowItem {
  id: string;
  title: string;
  subTitle?: string;
}

interface Props {
  item: ReasonRowItem;
  index: number;
  onEdit: (id: string, newTitle: string) => void;
  onDelete: (id: string) => void;
  tPrefix: string;
  isDragDisabled?: boolean;
  isLast?: boolean;
  shouldDisable?: boolean;
  isProtected?: boolean;
  protectedEditTooltip?: string;
}

const DraggableReasonItem: React.FC<Props> = ({
  item,
  index,
  onEdit,
  onDelete,
  tPrefix,
  isDragDisabled = false,
  isLast = false,
  shouldDisable = false,
  isProtected = false,
  protectedEditTooltip
}) => {
  const { t } = useTranslation();
  const [isEditing, setIsEditing] = useState(false);
  const [editingValue, setEditingValue] = useState(item.title);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (isEditing && inputRef.current) {
      inputRef.current.focus();
    }
  }, [isEditing]);

  const handleEdit = () => {
    setEditingValue(item.title);
    setIsEditing(true);
  };

  const handleSave = () => {
    onEdit(item.id, editingValue);
    setIsEditing(false);
  };

  const handleCancel = () => {
    setEditingValue(item.title);
    setIsEditing(false);
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter") {
      e.preventDefault();
      handleSave();
    } else if (e.key === "Escape") {
      e.preventDefault();
      handleCancel();
    }
  };

  const handleDelete = () => {
    onDelete(item.id);
  };

  const dropdownOptions: DetailedViewOptions = {
    edit: { action: handleEdit, disabled: isProtected, disabledTooltipText: protectedEditTooltip },
    delete: { action: handleDelete, variant: "destructive" as const, disabled: shouldDisable }
  };

  const content = (
    provided: DraggableProvided,
    snapshot: DraggableStateSnapshot,
    isClone: boolean = false
  ) => (
    <div
      ref={isClone ? null : provided.innerRef}
      {...provided.draggableProps}
      className={cn(
        "w-full flex flex-col items-start justify-center",
        "max-w-[800px] pl-1 pr-2 py-4 gap-4",
        "bg-surface-widget-card",
        !isLast && "border-b border-border-border1",
        "hover:bg-surface-hover-soft transition-colors duration-200",
        snapshot.isDragging && [
          "border-t border-b border-border-border1",
          "bg-surface-focus-selected",
          "shadow-[0px_12px_24px_-4px_rgba(0,0,0,0.20),0px_0px_2px_-2px_rgba(0,0,0,0.18)]",
          "relative z-50"
        ],
        "group"
      )}
      style={provided.draggableProps.style}
    >
      <div className="flex flex-row w-full justify-between items-center h-4">
        <div className="flex flex-row items-center w-full gap-0">
          <div
            {...provided.dragHandleProps}
            className={cn(
              "px-0 cursor-grab active:cursor-grabbing",
              "opacity-0 group-hover:opacity-100 transition-opacity"
            )}
          >
            {!isEditing && <GripVertical className="h-4 w-4 text-secondary" />}
          </div>
          <div className="flex flex-col w-full justify-start gap-1">
            {isEditing ? (
              <div className="flex w-full items-stretch gap-6 pl-1">
                <Input
                  ref={inputRef}
                  value={editingValue}
                  onChange={(e) => setEditingValue(e.target.value)}
                  onKeyDown={handleKeyDown}
                  autoFocus
                  containerClassName="flex-1"
                  className="h-8"
                />
                <div className="flex gap-2">
                  <Button
                    variant="secondary"
                    onClick={handleCancel}
                  >
                    {t("cancel")}
                  </Button>
                  <Button
                    onClick={handleSave}
                  >
                    {t("save")}
                  </Button>
                </div>
              </div>
            ) : (
              <Text variant="body">{item.title}</Text>
            )}
            {item.subTitle && (
              <Text variant="bodyTertiary">{item.subTitle}</Text>
            )}
          </div>
        </div>
        {!isEditing && (
          <OptionsDropdownMenu
            options={dropdownOptions}
            tPrefix={`${tPrefix}.dropdown`}
            showIcons={false}
            buttonVariant="iconNoBg"
            className="opacity-0 group-hover:opacity-100 transition-opacity duration-200"
          />
        )}
      </div>
    </div>
  );

  return (
    <Draggable
      draggableId={item.id}
      index={index}
      isDragDisabled={isDragDisabled || isEditing}
    >
      {(provided, snapshot) => (
        <>
          {content(provided, snapshot)}
          {snapshot.isDragging &&
            createPortal(content(provided, snapshot, true), document.body)}
        </>
      )}
    </Draggable>
  );
};

export default DraggableReasonItem;
